import { FeeSettingsComponent } from '../../../components/events/fee-settings-component.js';
import { ApplicationFeeProtocol } from './application-fee-protocol.js';
import { EventProfile } from '../event-profile.js';
import { Price } from '../price.js';
import { TicketTemplate } from '../ticket-template';
import { isNotNullOrUndefined } from '../../../utility/null-safety.js';
import { OnlineEventTicketOrder } from '../online-event-ticket-order.js';
import { Fee } from '../fee.js';
import { EventTicket } from '../event-ticket.js';
import { OnlineProductOrder } from '../online-product-order.js';
import { ProductOrderItem } from '../product-order-item.js';
import { Product } from '../product.js';
import { InvoiceVat } from '../invoicing/invoice-vat.js';
import { TicketSupplementTemplate } from '../ticket-supplement-template.js';
import { EventBookingStrategy } from '../event-booking-protocol/event-booking-strategy.js';
import { GenericOrderVoucherEntry } from '../orders/generic-order-entries/generic-order-voucher-entry.js';
import { GenericOrderProductEntry } from '../orders/generic-order-entries/generic-order-product-entry.js';
import { GenericOrderTicketEntry } from '../orders/generic-order-entries/generic-order-ticket-entry.js';

export class EventFeeProvider {
  private readonly standardFee: ApplicationFeeProtocol | null;
  private readonly productFee: ApplicationFeeProtocol | null;

  constructor(event: EventProfile) {
    const component = event.findComponentByTypeId<FeeSettingsComponent>(FeeSettingsComponent.TYPE_ID);
    this.standardFee = component?.standardFee ?? null;

    if(event.bookingStrategy === EventBookingStrategy.GenericSelling) {
      this.productFee = component?.productFee ?? this.standardFee;
    }
    else if(event.bookingStrategy === EventBookingStrategy.TicketsAndProducts) {
      this.productFee = component?.productFee ?? this.standardFee;
    }
    else {
      this.productFee = this.standardFee;
    }
  }

  getFeeVatOverrideForTickets(): InvoiceVat | null {
    return this.standardFee?.overrideVat ?? null;
  }

  getFeeVatOverrideForProducts(): InvoiceVat | null {
    return this.productFee?.overrideVat ?? null;
  }

  calculateFeeForTicketCartItem(options: {
    ticket: TicketTemplate,
    supplement?: TicketSupplementTemplate | null,
    overrideUnitPrice?: Price,
    quantity?: number,
  }): Price | null {
    let ticketPrice: Price = options.overrideUnitPrice ?? options.ticket.price;
    const overrideFee: Fee | null = options.ticket.applicationFee;
    const quantity: number | null = options.quantity ?? null;
    let fee: Price | null = null;

    if(options.supplement != null) {
      ticketPrice = ticketPrice.add(options.supplement.price);
    }

    if(isNotNullOrUndefined(overrideFee)) {
      fee = overrideFee.calculateUnitFee(ticketPrice);
    }
    else if(isNotNullOrUndefined(this.standardFee)) {
      fee = this.standardFee.calculateUnitFee(ticketPrice);
    }

    if(isNotNullOrUndefined(fee) && isNotNullOrUndefined(quantity)) {
      fee = fee.scale(quantity);
    }

    return fee?.isPositive() === true ? fee : null;
  }

  calculateFeeForProductCartItem(options: {
    product: Product,
    overrideUnitPrice?: Price,
    quantity?: number,
  }): Price | null {
    const productPrice: Price = options.overrideUnitPrice ?? options.product.price;
    const quantity: number | null = options.quantity ?? null;
    let fee: Price | null = null;

    if(isNotNullOrUndefined(this.productFee)) {
      fee = this.productFee.calculateUnitFee(productPrice);
    }

    if(isNotNullOrUndefined(fee) && isNotNullOrUndefined(quantity)) {
      fee = fee.scale(quantity);
    }

    return fee?.isPositive() === true ? fee : null;
  }

  calculateFeeForTicketOrder(order: OnlineEventTicketOrder): { ticketFee: Price | null, productFee: Price | null } {
    let ticketFee: Price | null = null;
    let productFee: Price | null = null;

    for(const ticket of order.tickets) {
      const fee = this.calculateFeeForEventTicket(ticket);
      if(isNotNullOrUndefined(fee)) {
        ticketFee = ticketFee != null ? ticketFee.add(fee) : fee;
      }
    }

    for(const product of order.products) {
      const fee = this.calculateFeeForProductOrderItem(product);
      if(isNotNullOrUndefined(fee)) {
        productFee = productFee != null ? productFee.add(fee) : fee;
      }
    }

    return { ticketFee, productFee };
  }

  calculateFeeForProductOrder(order: OnlineProductOrder): Price | null {
    let totalFee: Price | null = null;

    for(const item of order.items) {
      const fee = this.calculateFeeForProductOrderItem(item);
      if(isNotNullOrUndefined(fee)) {
        totalFee = totalFee != null ? totalFee.add(fee) : fee;
      }
    }

    return totalFee;
  }

  calculateFeeForTicketOrderEntry(entry: GenericOrderTicketEntry): Price | null {
    let totalFee: Price | null = null;

    for(const ticket of entry.tickets) {
      const fee = this.calculateFeeForEventTicket(ticket);
      if(isNotNullOrUndefined(fee)) {
        totalFee = totalFee != null ? totalFee.add(fee) : fee;
      }
    }

    return totalFee;
  }

  calculateFeeForProductOrderEntry(entry: GenericOrderProductEntry): Price | null {
    let totalFee: Price | null = null;

    for(const item of entry.products) {
      const fee = this.calculateFeeForProductOrderItem(item);
      if(isNotNullOrUndefined(fee)) {
        totalFee = totalFee != null ? totalFee.add(fee) : fee;
      }
    }

    return totalFee;
  }

  calculateFeeForVoucherOrderEntry(entry: GenericOrderVoucherEntry): Price | null {
    let totalFee: Price | null = null;

    for(const item of entry.products) {
      const fee = this.calculateFeeForProductOrderItem(item);
      if(isNotNullOrUndefined(fee)) {
        totalFee = totalFee != null ? totalFee.add(fee) : fee;
      }
    }

    return totalFee;
  }

  getFeeForEventTicket(ticket: EventTicket): Fee | null {
    const overrideFee: Fee | null = ticket.template.applicationFee;
    let ticketPrice: Price = ticket.template.price;

    if(ticket.supplement != null) {
      ticketPrice = ticketPrice.add(ticket.supplement.template.price);
    }

    return overrideFee ?? (this.standardFee?.getUnitFee(ticketPrice) ?? null);
  }

  getFeeForProductOrderItem(item: ProductOrderItem): Fee | null {
    const productPrice: Price = item.product.price;
    return this.productFee?.getUnitFee(productPrice) ?? null;
  }

  private calculateFeeForEventTicket(ticket: EventTicket): Price | null {
    const overrideFee: Fee | null = ticket.template.applicationFee;
    let ticketPrice: Price = ticket.template.price;
    let fee: Price | null = null;

    if(ticket.supplement != null) {
      ticketPrice = ticketPrice.add(ticket.supplement.template.price);
    }

    if(isNotNullOrUndefined(overrideFee)) {
      fee = overrideFee.calculateUnitFee(ticketPrice);
    }
    else if(isNotNullOrUndefined(this.standardFee)) {
      fee = this.standardFee.calculateUnitFee(ticketPrice);
    }

    return fee?.isPositive() === true ? fee : null;
  }

  private calculateFeeForProductOrderItem(item: ProductOrderItem): Price | null {
    const productPrice: Price = item.product.price;
    let fee: Price | null = null;

    if(isNotNullOrUndefined(this.productFee)) {
      fee = this.productFee.calculateUnitFee(productPrice);
    }
    if(isNotNullOrUndefined(fee)) {
      fee = fee.scale(item.quantity);
    }

    return fee?.isPositive() === true ? fee : null;
  }
}
